import _invoke from 'lodash/invoke';
import Store from 'lightningjs-redux';
import { thirdPartyWithLayoutService } from '@bedrock/fido-sdk';
import config from '@bedrock-rdk/config';
import { appVersion, appCustomer, appPlatform } from '@bedrock-rdk/utils';
import { getEngines } from './customer/default/engines';
import { getDeviceBrand, getDeviceModel, getDeviceVersion } from '../app/app.selectors';
import { getCurrentLayout } from '../layout/layout.selectors';
import { getUserState, getUid, getDeviceId, getHomeId } from '../user/user.selectors';
import { getPairingStatus } from '../pairing/pairing.selectors';
import { PAIRING_STATUS_PAIRED } from '../pairing/pairing.constants';
import { PROJECT_NAME } from '../app/app.constants';
import { getTenant, getEnv } from '../app/app.utils';
import { stopEngines } from './customer/default/utils';

export { startEngines } from './customer/default/utils';

/**
 * Fetches a list of engine descriptors available for current customer config and filters out disabled engines
 *
 * @param {string[]} enabledEngines list of engines keys that are enabled in some particular configuration
 * @returns {Object[]} list of engines descriptors
 */
export const getThirdPartyServicesConfigs = enabledEngines => {
  const engines = getEngines();

  return Array.isArray(engines) && Array.isArray(enabledEngines)
    ? engines
        .filter(({ thirdPartyServiceConfig: { key } }) => enabledEngines.includes(key))
        .map(engine => engine.thirdPartyServiceConfig)
    : [];
};

/**
 * Sets analytics attributes that should be kept for subsequent events tracking
 * @param {Object} attributes a set of persistent parameters for analytics engines
 */
export const setAttributes = attributes => {
  thirdPartyWithLayoutService.setCustomAttributes(attributes);
};

/**
 * Sends an event to all enabled engines
 * @param {Oject} event either an object containing a tagging plan key or analytics block from Layout API
 */
export const sendEvent = (event, action, attributes = {}) => {
  if (!event) {
    return;
  }

  // Search API uses generic platform code (m6group_web), overriding it
  const mappings = config.analytics.attributesKeysMapping;
  const keys = Object.keys(event);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const mapping = mappings[key];

    if (event[key] && mapping && mapping.platformCode) {
      event[key][mapping.platformCode] = config.platformCode;
    }
  }

  thirdPartyWithLayoutService.sendEvent(event, action || null, attributes);
};

/**
 * Sets user id attribute that should be kept for subsequent events
 * @param {Oject} userData  object that contains a UID key
 */
export const setUser = userData => {
  try {
    thirdPartyWithLayoutService.userChange(userData);
  } catch (error) {
    // TODO: error handling
    // eslint-disable-next-line no-console
    console.error('[AnalyticsUtils]: Can not set user UID');
  }
};

/**
 * Sets 'audienceMeasurement', 'personalization', 'targetedAdvertising' attributes that should be kept for subsequent events
 */
export const trackGDPRConsent = cookieConsents => {
  try {
    const { audienceMeasurement, personalization, targetedAdvertising } = cookieConsents;

    const deviceConsents = {
      tracking: { consent: audienceMeasurement },
      multiDeviceMatching: { consent: personalization },
      adTargeting: { consent: targetedAdvertising },
    };
    thirdPartyWithLayoutService.gdprChange(deviceConsents);
  } catch (error) {
    // TODO: error handling
    // eslint-disable-next-line no-console
    console.error('[AnalyticsUtils]: Can not set device consents');
  }
};

/**
 * Sends a page view event to all enabled engines
 * @param {string} pathname current router URL
 */
export const sendPageView = pathname => {
  thirdPartyWithLayoutService.sendPageViewEvent(null, { pathname }, {});
};

export const sendPrintEvent = (...args) => {
  thirdPartyWithLayoutService.sendPrintEvent(...args);
};

export const sendImpressionEvent = (layout, blocks, items) => {
  thirdPartyWithLayoutService.sendImpressionTrackingEvent(
    { list_of_block_viewed: blocks, list_of_item_viewed: items },
    'display',
    layout,
  );
};

export const getIsFirstConnection = () => {
  if (!localStorage.getItem('isFirstConnection')) {
    localStorage.setItem('isFirstConnection', 'yes');

    return 'yes';
  }
  localStorage.setItem('isFirstConnection', 'no');

  return 'no';
};

export const setInitialAttributes = state => {
  const { geo } = getUserState(state);

  setAttributes({
    deviceID: getDeviceId(state),
    userId: getUid(state),
    homeId: getHomeId(state),
    IPhash: geo ? geo.hashIp : null,
    platformCode: config.platformCode,
    language: config.language,
    // Define is the user logged in, after application is start and user is paired we can change dimension on yes
    authentified: getPairingStatus(state) === PAIRING_STATUS_PAIRED ? 'yes' : 'no',
    // Authentication method. Always set to "pairing" as the only possible on the OTT boxes platform.
    authMethod: 'pairing',
    entityType: 'app',
    // Defines if a user has an ad blocker or not. Not applicable to OTT boxes, so always set to "no".
    adBlockEnabled: false,
    userStatus: getPairingStatus(state) === PAIRING_STATUS_PAIRED ? 'connected' : 'unknown',
    deviceBrand: getDeviceBrand(state),
    firstConnexion: getIsFirstConnection(),
    userAgent: navigator.userAgent,
    deviceModel: getDeviceModel(state),
    deviceVersion: getDeviceVersion(state),
    // Box type (same as in the User API call)
    boxType: config.clientCode,
    project: PROJECT_NAME,
    appTenant: getTenant(),
    appEnv: getEnv(),
    appVersion,
    appCustomer,
    appPlatform,
  });
};

export const enginesNamesToEnabledStateMap = (engines, shouldEnable) =>
  engines.reduce((result, engine) => ({ ...result, [engine]: shouldEnable }), {});

export const cleanupEngines = () => {
  stopEngines();

  const engines = getEngines();
  let cleanedUp = 0;

  if (Array.isArray(engines)) {
    engines.forEach(engine => {
      try {
        _invoke(engine, 'cleanup');
      } finally {
        cleanedUp += 1;
      }
    });
  }

  // eslint-disable-next-line no-console
  console.log(`[analytics.utils] Engines cleaned up: ${cleanedUp}`);
};
// eslint-disable-next-line no-unused-vars

export const overrideGAPageName = pageName => {
  const state = Store.getState();
  const currentLayout = getCurrentLayout(state);

  if (currentLayout && currentLayout.analytics && currentLayout.analytics.googleAnalytics) {
    currentLayout.analytics.googleAnalytics.pageName = pageName;
  }
  if (currentLayout && currentLayout.analytics && currentLayout.analytics.tealium) {
    currentLayout.analytics.tealium.page_name = pageName;
  }
};
